import { useAtom } from 'jotai'
import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import {
  fullscreenAtom,
  ownsNFTAtom,
  rControlsVisibilityAtom,
  seekModeAtom,
  videoElementAtom,
} from '../atoms'
import MobileTimelineSeeker from './MobileTimelineSeeker'

//* =============================== Types =======================================

type MobileTimelineProps = {
  isVisible: boolean
}

//* ========================== React component ==================================

/**
 * The timeline for the mobile player.
 * Together with the timeline seeker, this component enables the user to seek around the video.
 *
 * Notes for fullscreen mode:
 * When in fullscreen mode, the timeline also gets hidden. This is not the case outside the
 * fullscreen mode.
 *
 */
const MobileTimeline = ({ isVisible }: MobileTimelineProps): JSX.Element => {
  const [seekMode] = useAtom(seekModeAtom)
  const [videoElement] = useAtom(videoElementAtom)
  const [controlsVisible] = useAtom(rControlsVisibilityAtom)
  controlsVisible
  const [ownsNFT] = useAtom(ownsNFTAtom)

  // We need a ref, otherwise the seekMode value will just get copied to the listener callbacks.
  const seekModeRef = useRef<boolean>(seekMode)
  useEffect(() => {
    seekModeRef.current = seekMode
  }, [seekMode])

  // We need to store the current time of the video element, because the time
  // updates are disabled when the seek mode is enabled.
  const [currentProgress, setCurrentProgress] = useState(0.0)

  const [isFullscreen] = useAtom(fullscreenAtom)

  useEffect(() => {
    if (!videoElement) return

    videoElement.addEventListener('timeupdate', () => {
      // Only update the current progress if the seek mode is disabled.
      if (!seekModeRef.current) {
        setCurrentProgress(videoElement ? videoElement.currentTime / videoElement.duration : 0)
      }
    })
  }, [videoElement])

  const ref = useRef<HTMLDivElement>(null)

  const [relativeBufferLength, setRelativeBufferLength] = useState(0.0)

  // Calculate the time range of the buffered video.
  useEffect(() => {
    if (!videoElement) return

    const timeRanges = videoElement.buffered
    if (timeRanges.length > 0) {
      const timeRange = timeRanges.end(timeRanges.length - 1)
      setRelativeBufferLength(timeRange / videoElement.duration)
    }
  }, [videoElement?.buffered])

  return (
    <BackgroundRectangle ref={ref} isFullscreen={isFullscreen} isVisible={isVisible}>
      <style>
        {`.foreground-rectangle {          
            border-radius: ${isFullscreen ? '100px' : '0px'};
            width: ${currentProgress * 100}%;
            z-index: 1;
            height: 100%;
            background: ${ownsNFT ? '#f2f2f2' : '#f2f2f2'};
            position: absolute;
            left: 0;
           }`}
      </style>
      <div className="foreground-rectangle" />
      {/* <style>
          {`.circle-head {
            opacity: ${controlsVisible ? '1' : '0'};
            right: 0;
            top: 50%;
            background: white;
            position: absolute;
            width: 12px;
            height: 12px;
            border-radius: 100px;
            transform: translate(50%, -50%);
            transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;


            :hover {
              transform: scale(1.3);
            }

            will-change: opacity;
          }`}
        </style>
        <div className="circle-head" /> */}
      <style>
        {`.buffer-rectangle {
              width: ${relativeBufferLength * 100}%;
              border-radius: ${isFullscreen ? '100px' : '0px'};
              height: 100%;
              background-color: #828282;
              position: absolute;
              left: 0;
              top: 0;
             }`}
      </style>
      <div className="buffer-rectangle" />
      <MobileTimelineSeeker reference={ref} />
    </BackgroundRectangle>
  )
}

export default MobileTimeline

//* ========================== Styled components ================================

const BackgroundRectangle = styled.div<{ isFullscreen: boolean; isVisible: boolean }>`
  position: relative;
  // We push the timeline down so it is bascially outside of the player.
  // Instead of just placing the component a t the bottom, we use absolute positioning.
  // This helps later, in fullscreen mode, to position the timeline correctly.
  width: ${({ isFullscreen }) => (isFullscreen ? '95%' : '85%')};
  border-radius: ${({ isFullscreen }) => (isFullscreen ? '100px' : '0px')};
  align-items: center;
  display: flex;
  flex-direction: row;
  transition: opacity 300ms ease-out, visibility linear 300ms;
  flex-grow: 10;
  height: 3px;
  background-color: #818181;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'all' : 'none')};
  user-select: none;
`
